import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Documents from './pages/Documents';
import DocumentDetail from './pages/DocumentDetail';
import Search from './pages/Search';
import Chatbot from './pages/Chatbot';
import Workflows from './pages/Workflows';
import Settings from './pages/Settings';
import AdminDashboard from './pages/AdminDashboard';
import Analytics from './pages/Analytics';
import AgenticExtensions from './pages/AgenticExtensions';
import VisionIntelligence from './pages/VisionIntelligence';
import EnterpriseIntelligence from './pages/EnterpriseIntelligence';
import TerminologyAdmin from './pages/TerminologyAdmin';
import SystemHealth from './pages/SystemHealth';
import AccuracyDashboard from './pages/AccuracyDashboard';
import ExecutiveCommandCenter from './pages/ExecutiveCommandCenter';
import Benchmarking from './pages/Benchmarking';
import BoardDashboard from './pages/BoardDashboard';
import DocumentAnnotator from './pages/DocumentAnnotator';
import SharedDocument from './pages/SharedDocument';
import RevenueDashboard from './pages/RevenueDashboard';
import RevenueAdmin from './pages/RevenueAdmin';
import TenantSubscription from './pages/TenantSubscription';
import SecurityCenter from './pages/SecurityCenter';
import FeatureGate from './components/FeatureGate';
import { QuotaExceededModal } from './components/QuotaExceededModal';
import { ErrorBoundary } from './components/ErrorBoundary';
import './i18n';
import './App.css';

const PrivateRoute = ({ children, roles }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Role-gated routes: redirect users who lack the required role to the dashboard.
  if (roles && !roles.some((r) => user?.roles?.includes(r))) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/shared" element={<SharedDocument />} />
      <Route path="/shared/:token" element={<SharedDocument />} />
      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/documents"
        element={
          <PrivateRoute>
            <Layout>
              <Documents />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/documents/:id"
        element={
          <PrivateRoute>
            <Layout>
              <DocumentDetail />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/search"
        element={
          <PrivateRoute>
            <Layout>
              <Search />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/chatbot"
        element={
          <PrivateRoute>
            <Layout>
              <FeatureGate feature="ai_qa">
                <Chatbot />
              </FeatureGate>
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/workflows"
        element={
          <PrivateRoute>
            <Layout>
              <Workflows />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <PrivateRoute>
            <Layout>
              <Settings />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <PrivateRoute>
            <Layout>
              <AdminDashboard />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <PrivateRoute>
            <Layout>
              <Analytics />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/ai-extensions"
        element={
          <PrivateRoute>
            <Layout>
              <FeatureGate modules={['Agentic AI', 'Agentic Extensions']}>
                <AgenticExtensions />
              </FeatureGate>
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/vision-intelligence"
        element={
          <PrivateRoute>
            <Layout>
              <FeatureGate modules={['Vision AI']}>
                <VisionIntelligence />
              </FeatureGate>
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/enterprise-intelligence"
        element={
          <PrivateRoute>
            <Layout>
              <FeatureGate modules={['Enterprise AI']}>
                <EnterpriseIntelligence />
              </FeatureGate>
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/terminology-admin"
        element={
          <PrivateRoute>
            <Layout>
              <TerminologyAdmin />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/system-health"
        element={
          <PrivateRoute>
            <Layout>
              <SystemHealth />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/accuracy"
        element={
          <PrivateRoute>
            <Layout>
              <AccuracyDashboard />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/executive"
        element={
          <PrivateRoute>
            <Layout>
              <ExecutiveCommandCenter />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/benchmarking"
        element={
          <PrivateRoute>
            <Layout>
              <Benchmarking />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/board"
        element={
          <PrivateRoute>
            <Layout>
              <BoardDashboard />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/documents/:id/annotate"
        element={
          <PrivateRoute>
            <Layout>
              <DocumentAnnotator />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <PrivateRoute>
            <Layout>
              <TenantSubscription />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/revenue"
        element={
          <PrivateRoute roles={['SuperAdmin']}>
            <Layout>
              <RevenueDashboard />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/revenue-admin"
        element={
          <PrivateRoute roles={['SuperAdmin']}>
            <Layout>
              <RevenueAdmin />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/security-center"
        element={
          <PrivateRoute roles={['SuperAdmin']}>
            <Layout>
              <SecurityCenter />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ErrorBoundary>
          <AppRoutes />
        </ErrorBoundary>
        <QuotaExceededModal />
        <Toaster position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;